/**
 * LineryMerch — Price Calculator, composant d'interface
 * -----------------------------------------------------
 * Élément personnalisé <linery-price-calculator>, repris des maquettes Figma
 * de Thea. Deux panneaux : la commande en gros et l'achat d'échantillon.
 *
 * Le calcul monétaire n'est pas ici — il est dans linery-pricing.js, testé à part.
 * Ce fichier lit l'état de l'interface, demande un devis, et l'affiche.
 *
 * Trois points méritent d'être lus avant de modifier quoi que ce soit.
 *
 * 1. Le curseur et les champs de taille sont réciproques. Saisir des quantités
 *    déplace le curseur ; déplacer le curseur reporte l'écart sur la taille
 *    active. Sans taille active, le curseur ne s'applique pas : les champs
 *    clignotent pour appeler un choix, et la quantité demandée reste en attente
 *    jusqu'à ce que le client désigne une taille.
 *
 * 2. L'ajout au panier part en deux requêtes quand une étiquette est jointe.
 *    Les vêtements passent en JSON par le tableau `items`, une ligne par taille.
 *    L'étiquette part seule, en FormData : c'est la seule forme que /cart/add.js
 *    accepte pour un fichier, et elle ne prend qu'un article à la fois.
 *
 * 3. Le fichier déposé par le client est porté par la ligne de panier. Shopify
 *    l'héberge et le rend téléchargeable depuis la commande dans l'admin — c'est
 *    ainsi que LineryMerch récupère les visuels après paiement, sans application.
 *
 * Dépendances : aucune.
 */
(function () {
  'use strict';

  var pricingPromise = null;
  function loadPricing(url) {
    if (!pricingPromise) {
      pricingPromise = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('linery-pricing.json : HTTP ' + r.status);
        return r.json();
      }).then(function (data) {
        // Tarif du client (badge « prix 3 » Agency, « prix 2 ») : sa grille n'est
        // jamais dans le fichier public, le thème la sort dans la page pour lui
        // seul. Sans elle, ou si elle est illisible, la grille standard.
        var bloc = document.getElementById('linery-tarif-data');
        if (!bloc || !window.LineryPricing || !window.LineryPricing.appliquerTarif) return data;
        try {
          return window.LineryPricing.appliquerTarif(data, JSON.parse(bloc.textContent));
        } catch (e) {
          return data;
        }
      });
    }
    return pricingPromise;
  }

  function norm(s) {
    var v = String(s == null ? '' : s).toLowerCase();
    if (v.normalize) v = v.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return v.replace(/[^a-z0-9]+/g, '');
  }

  function gsmNumber(s) {
    var m = String(s).match(/\d{2,4}/);
    return m ? parseInt(m[0], 10) : null;
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }

  function readJson(node) {
    if (!node || !node.textContent.trim()) return null;
    try { return JSON.parse(node.textContent); } catch (e) { return null; }
  }

  // ------------------------------------------------------------------ textes
  // Les textes que ce script ecrit lui-meme (boutons, retours, lignes de prix,
  // proprietes de ligne visibles). INC-06, 02/10/2026 : ils etaient en dur, en
  // francais, et restaient francais sur /en. Ils viennent desormais des
  // fichiers de langue (cles linery.calculator.js.*), par le bloc
  // <script data-linery-i18n> du snippet. Ce dictionnaire n'est que le repli :
  // les textes francais d'avant, mot pour mot (seul « 1 piece ... est deja au
  // panier » a ete accorde). Variables entre accolades, remplies par tr().
  var TEXTES = {
    variants_unreadable: "Matrice de variantes illisible.",
    pricing_unavailable: "Grille tarifaire indisponible.",
    product_unavailable: "Ce produit est indisponible pour le moment.",
    model_not_online: "Ce modele n'est pas encore en ligne.",
    qty_aria: "Quantité en taille {size}",
    tier_row: "{n} pièces",
    file_too_big: "Fichier trop lourd ({size} Mo, maximum {max} Mo).",
    file_uploaded: "Visuel déposé",
    label_none: "Aucune étiquette",
    per_piece: "{price} / pièce",
    from_per_piece: "à partir de {price} / pièce",
    saving: "−{percent} % sur le tarif d'entrée, soit {amount} d'économie",
    curiosity: "Curiosité tarifaire : {tier} pièces reviennent à {total}, soit moins cher que vos {qty} pièces actuelles.",
    nudge_one: "+{n} pièce pour passer à {price} l'unité (−{saving} par pièce).",
    nudge_other: "+{n} pièces pour passer à {price} l'unité (−{saving} par pièce).",
    in_cart_one: "{n} pièce de cette couleur est déjà au panier. Le palier {tier} s'applique sur les {total} pièces de cette configuration.",
    in_cart_other: "{n} pièces de cette couleur sont déjà au panier. Le palier {tier} s'applique sur les {total} pièces de cette configuration.",
    moq_note: "Minimum de commande : {moq} pièces pour cette couleur, toutes tailles confondues. Il en manque {missing}.",
    add_default: "Ajouter au panier",
    busy: "Ajout en cours…",
    select_quantities: "Sélectionnez vos quantités",
    min_for_color: "Minimum {moq} pièces pour cette couleur",
    quote: "Demander un devis",
    trade_access: "Accès professionnel requis",
    sample_max: "Maximum {max} par coloris et par modèle",
    sample_unavailable: "Échantillon indisponible",
    choose_size: "Choisissez une taille",
    combination_unavailable: "Combinaison indisponible",
    sample_add: "Ajouter au panier — {price}",
    applied_price_name: "Tarif appliqué",
    applied_price_value: "{price} / pièce (palier {tier})",
    variant_missing: "Variante introuvable pour : {sizes}",
    nothing_to_add: "Aucune quantité à ajouter.",
    added: "{n} pièces ajoutées au panier.",
    add_failed: "L'ajout au panier a échoué.",
    label_failed: "L'étiquette n'a pas pu être ajoutée.",
    add_refused: "Ajout refusé",
    sample_limit_one: "Vous avez déjà {max} échantillon de ce modèle dans ce coloris, le maximum.",
    sample_limit_other: "Vous avez déjà {max} échantillons de ce modèle dans ce coloris, le maximum.",
    sample_not_found: "Échantillon introuvable pour {model}. Écrivez-nous, nous vous l'envoyons.",
    sample_added: "Échantillon ajouté au panier.",
    sample_failed: "L'ajout a échoué.",
    cart_recap_one: "Panier : {n} pièce · {total}",
    cart_recap_other: "Panier : {n} pièces · {total}",
    prop_for: "Pour",
    prop_color: "Couleur",
    prop_size: "Taille",
  };

  function remplir(modele, vars) {
    return String(modele).replace(/\{(\w+)\}/g, function (tout, nom) {
      return vars && vars[nom] != null ? String(vars[nom]) : tout;
    });
  }

  /**
   * Les textes du bloc JSON, decodes : le filtre t de Shopify echappe les
   * traductions (« d&#39;entree »), et un <script> ne decode pas les entites.
   * Une cle absente, vide ou « translation missing » garde le repli francais.
   */
  function lireTextes(node) {
    var brut = readJson(node);
    if (!brut || typeof brut !== 'object') return null;
    var dec = document.createElement('textarea');
    var out = {};
    Object.keys(brut).forEach(function (k) {
      var v = brut[k];
      if (typeof v !== 'string' || !v || /^translation missing/i.test(v)) return;
      dec.innerHTML = v;
      out[k] = dec.value;
    });
    return out;
  }

  function routes(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + path;
  }

  /**
   * Shopify ne rend jamais plus de 250 variantes d'un coup — ni dans Liquid,
   * ni sur /products/<handle>.js. La fusion des grammages a fait passer huit
   * des quatorze familles au-dessus de ce plafond : CORE, c'est 34 couleurs
   * x 7 tailles x 3 grammages, soit 714 variantes. Resultat, le 220 GSM
   * n'existait tout simplement pas pour le visiteur, et la plupart des
   * couleurs du deuxieme grammage non plus.
   *
   * Le point d'entree accepte « ?page=2 ». On enchaine donc les pages tant
   * qu'une page en rend 250, avec une borne de securite : mieux vaut une
   * liste incomplete qu'une boucle sans fin si Shopify change d'avis.
   */
  /**
   * Fuseaux horaires des dix pays livres depuis le 16/09 (INC-07). Ils ne
   * servent qu'a preselectionner le pays de livraison dans la notice du mode
   * devis : si un pays s'ouvre ou se ferme, le pire est une suggestion
   * inutile, jamais un blocage.
   */
  var FUSEAUX_LIVRES = {
    'Europe/Paris': 'FR', 'Europe/Monaco': 'MC', 'Europe/Brussels': 'BE',
    'Europe/Luxembourg': 'LU', 'Europe/Berlin': 'DE', 'Europe/Busingen': 'DE',
    'Europe/Vienna': 'AT', 'Europe/Amsterdam': 'NL', 'Europe/Rome': 'IT',
    'Europe/Madrid': 'ES', 'Atlantic/Canary': 'ES', 'Africa/Ceuta': 'ES',
    'Europe/Lisbon': 'PT', 'Atlantic/Madeira': 'PT', 'Atlantic/Azores': 'PT',
  };

  var PAGE_VARIANTES = 250;
  var PAGES_MAX = 12;

  function pageProduit(handle, page) {
    var url = routes('products/' + encodeURIComponent(handle) + '.js');
    if (page > 1) url += '?page=' + page;
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  }

  /** Le produit avec TOUTES ses variantes, pages comprises. */
  function produitComplet(handle) {
    return pageProduit(handle, 1).then(function (p) {
      if (!p || !p.variants || p.variants.length < PAGE_VARIANTES) return p;
      var suite = function (page) {
        if (page > PAGES_MAX) return p;
        return pageProduit(handle, page).then(function (n) {
          var v = (n && n.variants) || [];
          if (!v.length) return p;
          p.variants = p.variants.concat(v);
          return v.length < PAGE_VARIANTES ? p : suite(page + 1);
        });
      };
      return suite(2);
    });
  }

  /** La matrice interne, depuis un produit renvoye par /products/<handle>.js */
  function matriceDepuis(p) {
    return {
      options: (p.options || []).map(function (o) { return o.name || o; }),
      variants: (p.variants || []).map(function (v) {
        return {
          id: v.id, o: v.options, available: v.available, price: v.price,
          // INC-07 : un stock non suivi n'est jamais « en rupture ».
          tracked: v.inventory_management === 'shopify',
        };
      }),
    };
  }

  /** Repère les index d'options d'un produit par le nom, insensible à la langue. */
  function optionIndexes(names) {
    var idx = { gsm: -1, color: -1, size: -1, model: -1 };
    (names || []).forEach(function (name, i) {
      var n = norm(name);
      // « Modele » est l'axe unique de la fiche echantillon : la famille et le
      // grammage y sont reunis dans une seule valeur, « CORE 160 g ».
      if (n.indexOf('modele') !== -1 || n.indexOf('model') !== -1) idx.model = i;
      else if (n.indexOf('gsm') !== -1 || n.indexOf('grammage') !== -1 ||
               n.indexOf('weight') !== -1 || n.indexOf('poids') !== -1) idx.gsm = i;
      else if (n.indexOf('couleur') !== -1 || n.indexOf('color') !== -1 || n.indexOf('colour') !== -1) idx.color = i;
      else if (n.indexOf('taille') !== -1 || n.indexOf('size') !== -1 || n.indexOf('format') !== -1) idx.size = i;
    });

    // LE PIEGE DU 19/09. Les noms d'options sont traduits par la boutique : sur
    // /en, « Grammage » devient « Weight » ou autre chose, et l'axe n'etait plus
    // reconnu. Consequences visibles : le selecteur de grammage restait cache et
    // currentVariants() ne filtrait plus rien, donc les sept tailles sortaient
    // une fois par grammage — vingt et une cases au lieu de sept.
    //
    // Ajouter des mots au dictionnaire ne suffit pas : la prochaine langue
    // cassera de nouveau. On raisonne donc par elimination. La couleur et la
    // taille se reconnaissent dans toutes les langues qui nous concernent ;
    // l'axe restant, quel que soit son nom, est le grammage.
    if (idx.gsm === -1 && idx.color !== -1 && idx.size !== -1) {
      for (var j = 0; j < (names || []).length; j++) {
        if (j !== idx.color && j !== idx.size && j !== idx.model) { idx.gsm = j; break; }
      }
    }

    return idx;
  }

  var Calculator = {

    // ================================================================ montage

    connected: function () {
      var self = this;
      this.textes = lireTextes(this.querySelector('[data-linery-i18n]'));

      this.state = {
        tab: 'details',
        gsm: null, color: null, sizes: {}, targetSize: null, pendingQty: null,
        label: { color: null, size: null, file: null },
        sample: { gsm: null, color: null, size: null },
        busy: false,
      };

      this.matrix = readJson(this.querySelector('[data-linery-variants]'));
      if (!this.matrix) return this.fatal(this.tr('variants_unreadable'));

      this.labelProduct = readJson(this.querySelector('[data-linery-label-product]'));
      this.sampleProduct = readJson(this.querySelector('[data-linery-sample-product]'));

      // Les index d'options sortent du meme optionIndexes que les deux autres
      // chemins, complement des variantes et changement de produit. Le gabarit
      // Liquid ne reconnait que « gsm » et « grammage » : sur /en l'option
      // s'appelle « Basis weight » et lui echappe. Les fiches de plus de 250
      // variantes etaient rattrapees par completerLesVariantes ; LAYER (36) et
      // FOLD (246) ne le sont jamais, et leurs tailles sortaient une fois par
      // grammage (constate le 25/09). Le gabarit ne sert plus que de repli.
      var parNom = optionIndexes(this.matrix.options);
      this.idx = {
        gsm: parNom.gsm !== -1 ? parNom.gsm : parseInt(this.dataset.idxGsm, 10),
        color: parNom.color !== -1 ? parNom.color : parseInt(this.dataset.idxColor, 10),
        size: parNom.size !== -1 ? parNom.size : parseInt(this.dataset.idxSize, 10),
        model: parNom.model,
      };
      this.labelIdx = this.labelProduct ? optionIndexes(this.labelProduct.options) : null;
      this.sampleIdx = this.sampleProduct ? optionIndexes(this.sampleProduct.options) : null;
      // LE PIEGE DU 30/09. Sur /en, Shopify traduit le nom de l'axe de la fiche
      // echantillon : « Modèle » y devient « Template ». Le nom n'etait plus
      // reconnu et sampleVariant() retombait sur la PREMIERE variante de la
      // fiche, BALANCE 340 g a 50 €, quel que soit le vetement choisi. Constate
      // sur une vraie commande : un echantillon de T-shirt CORE 160 g arrivait
      // a la caisse en hoodie BALANCE. La fiche echantillon n'a qu'un axe par
      // construction : dans ce cas, cet axe EST le modele, quel que soit son nom
      // et quelle que soit la langue.
      if (this.sampleIdx && (this.sampleProduct.options || []).length === 1) {
        this.sampleIdx = { gsm: -1, color: -1, size: -1, model: 0 };
      }

      this.key = this.dataset.pricingKey;
      this.handle = this.dataset.handle || null;
      // Voir le commentaire du snippet : grammage deduit du titre tant que
      // l'option n'existe pas sur le produit.
      this.gsmFallback = gsmNumber(this.dataset.gsmFallback);
      this.approved = this.dataset.approved === 'true';

      // Plafond d'echantillons par commande (devis du 10/07, section 2.5).
      // 0 ou valeur absente : pas de plafond.
      this.sampleMax = parseInt(this.dataset.sampleMax, 10);
      if (!(this.sampleMax > 0)) this.sampleMax = 0;
      this.mode = this.dataset.mode || 'display_only';
      this.uploadEnabled = this.dataset.upload !== 'disabled';
      this.uploadMax = (parseFloat(this.dataset.uploadMax) || 4.5) * 1024 * 1024;

      // Devises locales (01/10/2026). La grille tarifaire est en euros, la
      // devise de la boutique ; un visiteur au Royaume-Uni ou en Suisse voit
      // et paie en livres ou en francs. Shopify donne le taux de conversion
      // dans Shopify.currency.rate : tout montant issu de la grille passe par
      // enDevise() avant d'etre affiche. Le prix d'une variante, lui, arrive
      // deja converti. Taux 1 en euros : rien ne change pour la zone euro.
      var devise = window.Shopify && window.Shopify.currency;
      this.taux = (devise && devise.active === (this.dataset.currency || 'EUR'))
        ? (parseFloat(devise.rate) || 1) : 1;

      try {
        this.money = new Intl.NumberFormat(this.dataset.locale || 'fr', {
          style: 'currency', currency: this.dataset.currency || 'EUR',
        });
      } catch (e) {
        this.money = { format: function (v) { return v.toFixed(2) + ' €'; } };
      }

      this.bindTabs();
      this.bindModal();

      loadPricing(this.dataset.pricingUrl).then(function (data) {
        self.pricing = new window.LineryPricing(data);
        self.build();
      }).catch(function (e) {
        self.fatal(self.tr('pricing_unavailable'), e);
      });
    },

    /** Un texte de la langue de la page, variables remplies (INC-06). */
    tr: function (cle, vars) {
      var t = (this.textes && this.textes[cle]) || TEXTES[cle] || cle;
      return remplir(t, vars);
    },

    /** Singulier ou pluriel, regle francaise d'avant : pluriel au-dela de 1. */
    trn: function (cle, n, vars) {
      return this.tr(cle + (n > 1 ? '_other' : '_one'), vars);
    },

    /** Un pourcentage a une decimale, au format de la langue (8,2 / 8.2). */
    pourcent: function (v) {
      try {
        return new Intl.NumberFormat(this.dataset.locale || 'fr', { maximumFractionDigits: 1 }).format(v);
      } catch (e) {
        return String(v).replace('.', ',');
      }
    },

    fatal: function (msg, err) {
      if (window.console) console.error('[linery-price-calculator]', msg, err || '');
      this.feedback('[data-linery-feedback]', msg, true);
    },

    /**
     * Cablage : une fois par element, jamais deux. Tout ce qui pose un
     * ecouteur vit ici et nulle part ailleurs — sur la section d'accueil, ou
     * le visiteur change de produit sans recharger la page, un ecouteur pose
     * dans rebuild se dupliquerait a chaque changement.
     */
    build: function () {
      this.buildLabelOptions();
      this.bindLabel();
      this.bindTariff();
      this.bindPantone();
      this.bindSlider();
      this.bindStepper();
      this.bindSubmit();
      this.bindSampleSubmit();
      this.bindSwitch();
      this.buildProductPicker();
      this.bindProductPicker();
      this.bindQuoteMode();

      this.rebuild();

      // Un panier déjà commencé doit se voir dès l'arrivée sur la page.
      this.refreshCart(false);

      // Le gabarit Liquid s'arrete lui aussi a 250 variantes : sur les huit
      // familles qui depassent, la matrice posee dans la page est tronquee.
      this.completerLesVariantes();
    },

    /**
     * Va chercher les variantes que le gabarit n'a pas pu ecrire. On affiche
     * d'abord avec ce qu'on a — le visiteur voit la carte tout de suite — puis
     * on complete et on refait ce qui en depend.
     *
     * La selection en cours est preservee si elle reste valide : le complement
     * arrive quelques centaines de millisecondes apres l'affichage, le
     * visiteur a pu deja cliquer un grammage.
     */
    completerLesVariantes: function () {
      if (!this.handle || !this.matrix) return;
      if (this.matrix.variants.length < PAGE_VARIANTES) return;

      var self = this;
      var avant = this.matrix.variants.length;
      this.setBusy(true);

      return produitComplet(this.handle).then(function (p) {
        self.setBusy(false);
        var complete = matriceDepuis(p);
        if (complete.variants.length <= avant) return;

        self.matrix = complete;
        self.idx = optionIndexes(complete.options);

        var gsm = self.state.gsm;
        var couleur = self.state.color;
        self.rebuild();
        if (gsm && self.gsmValues().indexOf(gsm) !== -1) {
          self.state.color = couleur;
          self.selectGsm(gsm, true);
          self.render();
        }
      }).catch(function (e) {
        self.setBusy(false);
        if (window.console) console.error('[linery-price-calculator] completerLesVariantes', e);
      });
    },

    /**
     * Tout ce qui depend du produit courant : nom, grammages, couleurs,
     * tailles, prix. Appelee au demarrage, puis a chaque changement de
     * produit.
     */
    rebuild: function () {
      this.majModeDevis();
      var entry = this.pricing.entry(this.key, null);
      if (entry) {
        this.setTextAll('[data-linery-product-name]', entry.product.code);
        this.setTextAll('[data-linery-product-fit]', entry.product.fit);
        this.setTextAll('[data-linery-sample-name]', entry.product.code);
        this.setTextAll('[data-linery-sample-fit]', entry.product.fit);
      }

      this.buildGsm();
      this.selectGsm(this.gsmValues()[0], true);

      this.buildSampleGsm();
      this.selectSampleGsm(this.gsmValues()[0], true);

      this.render();
      this.renderSample();
    },

    /**
     * Change de produit sans recharger la page — c'est ce que demande la
     * section d'accueil, ou le modele se choisit dans une liste au lieu
     * d'etre impose par l'URL.
     *
     * La cle tarifaire EST le handle : depuis la fusion des grammages, une
     * famille correspond a une fiche et a un handle nu — « core », « fold ».
     * Le grammage n'etant plus dans le nom, rien a deduire.
     */
    switchProduct: function (handle) {
      var self = this;
      if (!handle || handle === this.handle) return Promise.resolve();
      this.setBusy(true);
      return produitComplet(handle).then(function (p) {
        self.handle = handle;
        self.matrix = matriceDepuis(p);
        self.idx = optionIndexes(self.matrix.options);
        self.key = handle;
        self.gsmFallback = gsmNumber(p.title);

        // On repart d'une configuration vierge : garder la taille ou la
        // couleur d'un autre modele donnerait une combinaison inexistante.
        self.state.gsm = null;
        self.state.color = null;
        self.state.sizes = {};
        self.state.targetSize = null;
        self.state.pendingQty = null;
        self.state.sample = { gsm: null, color: null, size: null };

        var vignette = self.thumbOf(p);
        self.querySelectorAll('[data-linery-product-thumb]').forEach(function (n) {
          self.setThumb(n, vignette);
        });
        self.markProductPickers();
        self.rebuild();
        self.setBusy(false);
      }).catch(function (e) {
        self.setBusy(false);
        self.feedback('[data-linery-feedback]', self.tr('product_unavailable'), true);
        if (window.console) console.error('[linery-price-calculator] switchProduct', e);
      });
    },

    /**
     * Le menu des produits se construit depuis la GRILLE TARIFAIRE, celle
     * extraite du portfolio. C'est deja la source de verite des prix, des
     * couleurs et des grammages ; en faire aussi la source de la liste evite
     * d'entretenir un second inventaire quelque part, qui finirait par
     * diverger. Une famille ajoutee au portfolio apparait ici sans qu'on
     * touche au theme.
     *
     * La cle de la grille est le handle de la fiche : « core » -> /products/core.
     * C'est la fusion des grammages qui rend cette egalite vraie.
     */
    buildProductPicker: function () {
      var hotes = this.querySelectorAll('[data-linery-product-menu]');
      if (!hotes.length) return;
      var self = this;
      hotes.forEach(function (host) { self.remplirMenuProduits(host); });
    },

    remplirMenuProduits: function (host) {
      var produits = (this.pricing.raw && this.pricing.raw.products) || {};
      host.textContent = '';
      Object.keys(produits).forEach(function (cle) {
        var p = produits[cle];
        var b = el('button', 'linery-pc__option linery-pc__product-option');
        b.type = 'button';
        b.dataset.handle = cle;
        b.setAttribute('data-linery-product-option', '');
        if (cle === this.handle) b.classList.add('is-active');

        var vignette = el('span', 'linery-pc__product-thumb');
        vignette.dataset.handle = cle;
        b.appendChild(vignette);

        var texte = el('span', 'linery-pc__product-label');
        texte.appendChild(el('b', 'linery-pc__product-name', p.code));
        texte.appendChild(el('span', 'linery-pc__product-fit', p.fit));
        b.appendChild(texte);

        host.appendChild(b);
      }, this);
    },

    /**
     * Les vignettes n'arrivent qu'a la premiere ouverture du menu. Charger
     * quatorze fiches produit au chargement de la page d'accueil serait
     * quatorze requetes pour une liste que le visiteur n'ouvrira peut-etre
     * jamais.
     *
     * Cette meme requete sert de verification : la grille tarifaire vient du
     * portfolio, elle ignore l'etat de la boutique. Une famille depubliee ou
     * archivee repond 404 — sa ligne est alors desactivee plutot que de mener
     * a un message d'erreur apres le clic.
     */
    loadProductThumbs: function () {
      if (this.thumbsLoaded) return;
      this.thumbsLoaded = true;
      var self = this;
      this.querySelectorAll('[data-linery-product-option]').forEach(function (btn) {
        var h = btn.dataset.handle;
        if (!h) return;
        var vignette = btn.querySelector('.linery-pc__product-thumb');
        fetch(routes('products/' + encodeURIComponent(h) + '.js'))
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            if (p) { self.setThumb(vignette, self.thumbOf(p)); return; }
            self.disableProduct(btn);
          })
          .catch(function () { self.disableProduct(btn); });
      });
    },

    disableProduct: function (btn) {
      if (!btn || btn.dataset.handle === this.handle) return;
      btn.disabled = true;
      btn.classList.add('is-unavailable');
      btn.title = this.tr('model_not_online');
    },

    thumbOf: function (p) {
      var src = p.featured_image || (p.images && p.images[0]) || null;
      return src ? String(src).replace(/^https?:/, '') : null;
    },

    setThumb: function (node, src) {
      if (!node || !src) return;
      node.style.backgroundImage = 'url(' + src + ')';
    },

    /**
     * Deux selecteurs peuvent coexister — un par onglet — et ils pilotent le
     * meme composant. Le cablage est donc pose sur chacun, et l'etat actif est
     * recopie dans les deux a chaque changement.
     */
    bindProductPicker: function () {
      var self = this;
      var racines = this.querySelectorAll('[data-linery-product-picker]');
      if (!racines.length) return;

      racines.forEach(function (racine) {
        var toggle = racine.querySelector('[data-linery-product-toggle]');
        var menu = racine.querySelector('[data-linery-product-menu]');
        if (!toggle || !menu) return;

        // Un seul menu ouvert a la fois : sans closeAllMenus, ouvrir la liste
        // des produits laissait celle des couleurs deroulee derriere.
        toggle.addEventListener('click', function () {
          var ouvrir = menu.hidden;
          self.closeAllMenus();
          menu.hidden = !ouvrir;
          toggle.setAttribute('aria-expanded', ouvrir ? 'true' : 'false');
          if (ouvrir) self.loadProductThumbs();
        });

        racine.addEventListener('click', function (e) {
          var btn = e.target && e.target.closest && e.target.closest('[data-linery-product-option]');
          if (!btn) return;
          e.preventDefault();
          self.closeAllMenus();
          self.switchProduct(btn.dataset.handle);
        });
      });

      document.addEventListener('click', function (e) {
        // Un clic n'importe ou hors d'un menu deroulant les ferme tous, y
        // compris ailleurs dans la carte. Se limiter a l'exterieur du
        // composant laissait le menu ouvert quand on cliquait sur le fond de
        // la carte ou sur une etiquette : le visiteur croit qu'il est bloque.
        var dansUnSelect = e.target && e.target.closest && e.target.closest('.linery-pc__select');
        if (!dansUnSelect) self.closeAllMenus();
      });
    },

    /** L'etat actif, recopie dans tous les menus de produit. */
    markProductPickers: function () {
      var courant = this.handle;
      this.querySelectorAll('[data-linery-product-option]').forEach(function (b) {
        b.classList.toggle('is-active', b.dataset.handle === courant);
      });
    },

    setBusy: function (on) {
      this.classList.toggle('is-loading', !!on);
    },

    // ================================================================ mode devis

    /**
     * INC-07, 01/10/2026. Tout le catalogue est fabrique a la commande : aucune
     * variante n'a de stock suivi. Une variante non suivie que Shopify declare
     * indisponible ne manque donc pas : Shopify refuse simplement de la vendre
     * dans le contexte du visiteur. En pratique, son pays n'a pas de tarif de
     * livraison (dix pays livres depuis le 16/09 ; un visiteur du Royaume-Uni,
     * de Suisse ou des Etats-Unis voyait tout le catalogue « Sold out » et ne
     * pouvait saisir aucune quantite). Meme mecanisme que le profil
     * d'expedition sans tarif du 10/09.
     *
     * Quand TOUT le produit est dans ce cas, le calculateur passe en mode
     * devis : tailles saisissables, notice explicite, et « Demander un devis »
     * a la place d'un ajout au panier que Shopify refuserait. Le jour ou un
     * pays recoit un tarif de livraison, le produit redevient disponible et
     * le mode devis s'eteint seul, sans toucher au code.
     */
    majModeDevis: function () {
      var vs = (this.matrix && this.matrix.variants) || [];
      this.quoteMode = vs.length > 0 && vs.every(function (v) { return !v.available && !v.tracked; });
      var on = this.quoteMode;
      this.classList.toggle('is-quote-mode', on);
      this.querySelectorAll('[data-linery-closed]').forEach(function (n) { n.hidden = !on; });
      if (on) this.suggererPays();
    },

    /**
     * Des clients EN FRANCE sont localises ailleurs par Shopify (signale par
     * Lohan le 01/10) : reseau d'entreprise, VPN, et forfait mobile suisse des
     * frontaliers (Linery est a Fillinges, a cote de Geneve). Le fuseau
     * horaire de l'appareil, lui, suit le lieu reel. S'il designe un pays
     * livre, ce pays est preselectionne et place avant le bouton de devis :
     * un clic sur « Valider » rouvre la commande en ligne. Une seule fois,
     * pour ne jamais ecraser un choix fait a la main.
     */
    suggererPays: function () {
      if (this.paysSuggere) return;
      var choix = this.querySelector('[data-linery-country]');
      if (!choix) return;
      this.paysSuggere = true;
      var code = FUSEAUX_LIVRES[this.fuseauHoraire()];
      if (!code || code === choix.value) return;
      var existe = Array.prototype.some.call(choix.options, function (o) { return o.value === code; });
      if (!existe) return;
      choix.value = code;
      var bloc = choix.closest('.linery-pc__closed-country');
      var lien = this.querySelector('[data-linery-quote-link]');
      if (bloc) {
        bloc.classList.add('is-suggested');
        if (lien && lien.parentNode === bloc.parentNode) bloc.parentNode.insertBefore(bloc, lien);
      }
    },

    /** Isole pour les tests. */
    fuseauHoraire: function () {
      try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
    },

    /** Une variante se choisit si elle se vend, ou si seul le pays bloque. */
    selectable: function (v) {
      return !!v && (v.available !== false || (!!this.quoteMode && !v.tracked));
    },

    bindQuoteMode: function () {
      var self = this;
      this.querySelectorAll('[data-linery-quote-link]').forEach(function (a) {
        a.addEventListener('click', function () {
          a.href = self.lienDevis(self.state.tab === 'sample' ? 'echantillon' : 'commande');
        });
      });
      var choix = this.querySelector('[data-linery-country]');
      var valider = this.querySelector('[data-linery-country-apply]');
      if (choix && valider) {
        valider.addEventListener('click', function () { self.changerDePays(choix.value); });
      }
    },

    /**
     * Le visiteur livre ailleurs que la ou on le localise (VPN, proxy
     * d'entreprise, bureau en France) : meme formulaire que le selecteur de
     * langue de l'en-tete. Il est cree hors du formulaire produit, dans
     * lequel le calculateur est rendu : deux formulaires imbriques sont
     * invalides et le navigateur ignore le second.
     */
    changerDePays: function (code) {
      if (!code) return;
      var modele = document.querySelector('form[action$="/localization"]');
      var f = document.createElement('form');
      f.method = 'post';
      f.action = modele ? modele.getAttribute('action') : routes('localization');
      [['form_type', 'localization'], ['utf8', '✓'], ['_method', 'put'],
       ['return_to', window.location.pathname], ['country_code', code]].forEach(function (c) {
        var i = document.createElement('input');
        i.type = 'hidden';
        i.name = c[0];
        i.value = c[1];
        f.appendChild(i);
      });
      document.body.appendChild(f);
      f.submit();
    },

    /** La page devis, avec la configuration composee en texte libre. */
    lienDevis: function (nature) {
      var base = this.dataset.quoteUrl || routes('pages/devis-merchandising');
      var texte = this.resumeConfiguration(nature);
      if (!texte) return base;
      return base + (base.indexOf('?') === -1 ? '?' : '&') + 'config=' + encodeURIComponent(texte);
    },

    resumeConfiguration: function (nature) {
      var entry = this.pricing ? this.pricing.entry(this.key, null) : null;
      var modele = entry ? entry.product.code : String(this.handle || '').toUpperCase();
      var lignes = [this.dataset.quoteIntro || 'Configuration :'];

      if (nature === 'echantillon') {
        var s = this.state.sample;
        lignes.push((this.dataset.quoteSample || 'Échantillon') + ' : ' + [modele + (s.gsm ? ' ' + s.gsm : ''),
          s.color ? this.libelleCouleurEchantillon() : null, s.size].filter(Boolean).join(', '));
        return lignes.join('\n');
      }

      var c = this.paletteEntry(this.state.color);
      var couleur = c && /^\d+$/.test(c.code) ? c.code + ' ' + c.name : this.state.color;
      lignes.push([modele + (this.state.gsm ? ' ' + this.state.gsm : ''), couleur].filter(Boolean).join(', '));
      var tailles = [], total = 0;
      Object.keys(this.state.sizes || {}).forEach(function (k) {
        var n = this.state.sizes[k];
        if (n > 0) { tailles.push(k + ' × ' + n); total += n; }
      }, this);
      if (tailles.length) lignes.push(tailles.join(', ') + ' (' + total + ' ' + (this.dataset.quotePieces || 'pièces') + ')');
      if (this.labelReady()) {
        lignes.push((this.dataset.quoteLabelWord || 'Étiquette') + ' : ' + [this.state.label.color, this.state.label.size].join(', '));
      }
      return lignes.join('\n');
    },

    demanderUnDevis: function (nature) {
      this.aller(this.lienDevis(nature));
    },

    /** Isole pour les tests : jsdom ne sait pas naviguer. */
    aller: function (url) { window.location.href = url; },

    // ================================================================ onglets

    bindTabs: function () {
      var self = this;
      this.querySelectorAll('[data-linery-tab]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.selectTab(btn.dataset.lineryTab); });
      });
    },

    selectTab: function (name) {
      this.state.tab = name;
      this.querySelectorAll('[data-linery-tab]').forEach(function (b) {
        b.setAttribute('aria-selected', b.dataset.lineryTab === name ? 'true' : 'false');
      });
      this.querySelectorAll('[data-linery-panel]').forEach(function (p) {
        p.hidden = p.dataset.lineryPanel !== name;
      });
    },

    // ================================================================ grammage

    gsmValues: function () {
      if (this.idx.gsm < 0) return [this.gsmFallback ? this.gsmFallback + ' GSM' : null];
      var seen = [];
      this.matrix.variants.forEach(function (v) {
        var val = v.o[this.idx.gsm];
        if (seen.indexOf(val) === -1) seen.push(val);
      }, this);
      return seen.sort(function (a, b) { return (gsmNumber(a) || 0) - (gsmNumber(b) || 0); });
    },

    gsmChip: function (val) {
      var b = el('button', 'linery-pc__chip');
      b.type = 'button';
      b.setAttribute('role', 'radio');
      b.dataset.value = val;
      var n = gsmNumber(val);
      if (n !== null) {
        b.appendChild(document.createTextNode(String(n) + ' '));
        b.appendChild(el('span', null, String(val).replace(String(n), '').trim() || 'GSM'));
      } else {
        b.textContent = val;
      }
      return b;
    },

    /**
     * La rangee des grammages se vide et se reconstruit a CHAQUE appel, et
     * elle s'affiche des qu'il y a au moins un grammage a montrer.
     *
     * Deux erreurs se sont succede ici, toutes deux visibles sur la section
     * d'accueil ou l'on change de modele sans recharger la page.
     *
     * La premiere : la sortie anticipee partait AVANT le nettoyage. En passant
     * d'une famille a trois grammages a une famille qui n'en a qu'un, les
     * pastilles de la precedente restaient a l'ecran — l'EDGE, qui n'existe
     * qu'en 340, s'affichait en 190/220/250 herites du EASE, ou en 340/450
     * herites du CURVE. Ces pastilles fantomes gardaient le gestionnaire de
     * clic de l'ancien produit : cliquer un 450 inexistant demandait les
     * couleurs d'un grammage absent du catalogue et le coloris retombait sur
     * « — ».
     *
     * La seconde : en corrigeant, on a garde l'idee que « un seul grammage
     * n'est pas un choix, donc on masque la rangee ». C'est faux. Le grammage
     * est une information produit avant d'etre un choix : un client qui
     * configure un EDGE doit lire 340 quelque part. La rangee reste donc
     * visible avec sa pastille unique, marquee « is-only » pour que le curseur
     * n'invite pas a cliquer sur ce qui ne se choisit pas.
     *
     * On ne masque plus que dans un seul cas : aucun grammage connu, ni par
     * les options du produit ni par le repli sur le titre.
     */
    buildGsm: function () {
      var values = this.gsmValues().filter(Boolean);
      var champ = this.querySelector('[data-linery-gsm-field]');
      var host = this.querySelector('[data-linery-gsm]');
      if (host) host.textContent = '';
      if (!values.length) {
        if (champ) champ.hidden = true;
        return;
      }
      if (champ) champ.hidden = false;
      if (!host) return;
      values.forEach(function (val) {
        var b = this.gsmChip(val);
        if (values.length === 1) b.classList.add('is-only');
        b.addEventListener('click', function () { this.selectGsm(val); }.bind(this));
        host.appendChild(b);
      }, this);
    },

    selectGsm: function (val, silent) {
      this.state.gsm = val;
      this.markActive('[data-linery-gsm] .linery-pc__chip', val);

      this.buildColors();
      var colors = this.colorValuesFor(val);
      this.selectColor(colors.indexOf(this.state.color) !== -1 ? this.state.color : colors[0], true);
      if (!silent) this.render();
    },

    markActive: function (selector, val) {
      this.querySelectorAll(selector).forEach(function (b) {
        var on = b.dataset.value === String(val);
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    },

    // ================================================================ couleurs

    colorValuesFor: function (gsmVal) {
      var out = [], seen = {};
      this.matrix.variants.forEach(function (v) {
        if (this.idx.gsm >= 0 && gsmVal !== null && v.o[this.idx.gsm] !== gsmVal) return;
        var c = v.o[this.idx.color];
        if (!seen[c]) { seen[c] = true; out.push(c); }
      }, this);

      var order = this.pricing.colorsFor(this.key, gsmNumber(gsmVal));
      if (!order.length) return out;
      var rank = {};
      order.forEach(function (c, i) { rank[norm(c.name)] = i; rank[norm(c.handle)] = i; });
      return out.sort(function (a, b) {
        var ra = rank[norm(a)], rb = rank[norm(b)];
        if (ra === undefined && rb === undefined) return 0;
        if (ra === undefined) return 1;
        if (rb === undefined) return -1;
        return ra - rb;
      });
    },

    /**
     * Retrouve la teinte de la palette derrière une valeur d'option.
     *
     * La correspondance est stricte, et ce n'est pas de la coquetterie : une
     * comparaison par sous-chaîne faisait passer « Off-white » pour « White »,
     * puisque l'un contient l'autre. Le client aurait vu la mauvaise pastille
     * et la mauvaise étiquette. On n'accepte donc que l'égalité du nom, du
     * handle, ou la forme numérotée du catalogue — « 02 Off-white ».
     */
    paletteEntry: function (value) {
      var palette = (this.pricing.raw.colors) || {};
      var n = norm(value);
      var numbered = String(value).match(/^\s*(\d{1,2})\s+(.+)$/);

      // Le code a deux chiffres est l'identifiant de la marque, et il ne
      // depend pas de la langue. Les valeurs d'option de la boutique sont en
      // francais — « 02 Blanc casse » — quand la palette du portfolio est en
      // anglais — « 02 Off-white » : comparer les noms ne trouvait jamais
      // rien, la pastille restait transparente et la reference Pantone
      // introuvable. On rapproche donc par le code, ce qui est plus strict
      // qu'une comparaison de noms, pas moins ; et on garde le libelle de la
      // boutique pour l'affichage, pour ne pas afficher l'anglais en francais.
      if (numbered) {
        for (var num in palette) {
          if (!Object.prototype.hasOwnProperty.call(palette, num)) continue;
          if (parseInt(num, 10) !== parseInt(numbered[1], 10)) continue;
          var pc = palette[num];
          return {
            code: num,
            name: numbered[2].trim(),
            hex: pc.hex,
            pantone: pc.pantone || null,
            reference: pc.name,
          };
        }
      }

      for (var code in palette) {
        if (!Object.prototype.hasOwnProperty.call(palette, code)) continue;
        var c = palette[code];
        var hit = norm(c.name) === n || norm(c.handle) === n;
        if (!hit && numbered) {
          hit = parseInt(numbered[1], 10) === parseInt(code, 10) && norm(numbered[2]) === norm(c.name);
        }
        if (hit) return { code: code, name: c.name, hex: c.hex, pantone: c.pantone || null };
      }
      return null;
    },

    /** Construit une liste déroulante de couleurs et renvoie sa fonction de sélection. */
    wireColorSelect: function (opts) {
      var self = this;
      var toggle = this.querySelector(opts.toggle);
      var menu = this.querySelector(opts.menu);
      if (!toggle || !menu) return;

      if (!toggle.dataset.wired) {
        toggle.dataset.wired = '1';
        toggle.addEventListener('click', function () {
          var open = menu.hidden;
          self.closeAllMenus();
          menu.hidden = !open;
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }

      menu.textContent = '';
      opts.values.forEach(function (val) {
        var entry = self.paletteEntry(val);
        var b = el('button', 'linery-pc__option');
        b.type = 'button';
        b.setAttribute('role', 'option');
        b.dataset.value = val;

        var dot = el('span', 'linery-pc__dot');
        if (entry) dot.style.setProperty('--dot', entry.hex);
        b.appendChild(dot);

        if (entry && /^\d+$/.test(entry.code)) {
          b.appendChild(el('span', 'linery-pc__num', entry.code + ' '));
          b.appendChild(el('span', null, entry.name));
        } else {
          b.appendChild(el('span', null, val));
        }

        b.addEventListener('click', function () {
          opts.onSelect(val);
          menu.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        });
        menu.appendChild(b);
      });
    },

    closeAllMenus: function () {
      this.querySelectorAll('.linery-pc__menu').forEach(function (m) { m.hidden = true; });
      this.querySelectorAll('[aria-expanded]').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    },

    buildColors: function () {
      this.wireColorSelect({
        toggle: '[data-linery-color-toggle]',
        menu: '[data-linery-colors]',
        values: this.colorValuesFor(this.state.gsm),
        onSelect: this.selectColor.bind(this),
      });
      this.buildPantone();
    },

    selectColor: function (val, silent) {
      this.state.color = val;
      var entry = this.paletteEntry(val);
      this.setText('[data-linery-color-name]', entry && /^\d+$/.test(entry.code) ? entry.code + ' ' + entry.name : (val || '—'));
      var dot = this.querySelector('[data-linery-color-dot]');
      if (dot) dot.style.setProperty('--dot', entry ? entry.hex : 'transparent');
      this.markPantone();

      this.querySelectorAll('[data-linery-colors] .linery-pc__option').forEach(function (b) {
        b.classList.toggle('is-active', b.dataset.value === String(val));
      });

      this.buildSizes();

      // La galerie ecoute pour remonter la face avant et la face arriere du
      // coloris choisi. `silent` est vrai au premier rendu : la page ne doit
      // pas sauter toute seule au chargement, seulement sur un clic.
      var code = (entry && entry.code) || (String(val || '').match(/^\s*(\d{1,2})/) || [])[1] || null;
      document.dispatchEvent(new CustomEvent('linery:color:changed', {
        detail: { value: val, code: code, silent: !!silent }
      }));

      if (!silent) this.render();
    },

    // ================================================================ tailles

    currentVariants: function () {
      var out = [];
      this.matrix.variants.forEach(function (v) {
        if (this.idx.gsm >= 0 && this.state.gsm !== null && v.o[this.idx.gsm] !== this.state.gsm) return;
        if (v.o[this.idx.color] !== this.state.color) return;
        out.push(v);
      }, this);
      return out;
    },

    /**
     * Ce que la configuration courante — produit, grammage, couleur — pèse déjà
     * au panier. On compare par identifiant de variante : c'est exact, et cela
     * ne dépend ni du nom des options ni de leur traduction.
     */
    inCartQty: function () {
      if (!this.cart || !this.cart.items) return 0;
      var ids = {};
      this.currentVariants().forEach(function (v) { ids[v.id] = true; });
      var n = 0;
      this.cart.items.forEach(function (i) { if (ids[i.variant_id]) n += i.quantity; });
      return n;
    },

    buildSizes: function () {
      var host = this.querySelector('[data-linery-sizes]');
      host.textContent = '';
      var kept = {};

      this.currentVariants().forEach(function (v) {
        var size = v.o[this.idx.size];
        // On conserve la ventilation quand le client compare deux teintes.
        var choisissable = this.selectable(v);
        kept[size] = choisissable ? (this.state.sizes[size] || 0) : 0;

        var wrap = el('div', 'linery-pc__size' + (choisissable ? '' : ' is-unavailable'));
        wrap.dataset.size = size;
        wrap.appendChild(el('span', 'linery-pc__size-name', size));

        var input = document.createElement('input');
        input.type = 'number';
        input.min = '0';
        input.step = '1';
        input.inputMode = 'numeric';
        input.value = String(kept[size]);
        input.dataset.size = size;
        input.setAttribute('aria-label', this.tr('qty_aria', { size: size }));
        if (!choisissable) input.disabled = true;

        if (choisissable) {
          input.addEventListener('focus', function () { this.targetSize(size); }.bind(this));
          input.addEventListener('click', function () { this.targetSize(size); }.bind(this));
          input.addEventListener('input', function () {
            var n = parseInt(input.value, 10);
            this.state.sizes[size] = Math.max(0, isFinite(n) ? Math.floor(n) : 0);
            this.state.targetSize = size;
            this.stopCalling();
            this.render();
          }.bind(this));
          input.addEventListener('blur', function () {
            if (input.value === '') { input.value = '0'; this.state.sizes[size] = 0; this.render(); }
          }.bind(this));
        }

        wrap.appendChild(input);
        host.appendChild(wrap);
      }, this);

      this.state.sizes = kept;
      if (this.state.targetSize && kept[this.state.targetSize] === undefined) this.state.targetSize = null;
    },

    /**
     * Désigne la taille que le curseur pilote. Si une quantité attendait
     * une taille, elle s'applique aussitôt — c'est le geste que décrit la maquette.
     */
    targetSize: function (size) {
      this.state.targetSize = size;
      if (this.state.pendingQty !== null) {
        var pending = this.state.pendingQty;
        this.state.pendingQty = null;
        this.stopCalling();
        this.setTotal(pending);
        return;
      }
      this.stopCalling();
      this.markTarget();
    },

    markTarget: function () {
      var t = this.state.targetSize;
      this.querySelectorAll('[data-linery-sizes] .linery-pc__size').forEach(function (n) {
        n.classList.toggle('is-target', n.dataset.size === t);
      });
    },

    startCalling: function () {
      var host = this.querySelector('[data-linery-sizes]');
      var hint = this.querySelector('[data-linery-size-call]');
      if (host) host.classList.add('is-calling');
      if (hint) hint.hidden = false;
    },

    stopCalling: function () {
      var host = this.querySelector('[data-linery-sizes]');
      var hint = this.querySelector('[data-linery-size-call]');
      if (host) host.classList.remove('is-calling');
      if (hint) hint.hidden = true;
    },

    totalQty: function () {
      var t = 0;
      for (var s in this.state.sizes) {
        if (Object.prototype.hasOwnProperty.call(this.state.sizes, s)) t += this.state.sizes[s] || 0;
      }
      return t;
    },

    /**
     * Porte la quantité totale à `value`, en reportant l'écart sur la taille active.
     * Sans taille active, la valeur reste en attente et les champs clignotent.
     */
    setTotal: function (value) {
      value = Math.max(0, Math.floor(value) || 0);
      var target = this.state.targetSize;

      if (!target || this.state.sizes[target] === undefined) {
        this.state.pendingQty = value;
        this.startCalling();
        this.render();
        return;
      }

      var others = this.totalQty() - (this.state.sizes[target] || 0);
      this.state.sizes[target] = Math.max(0, value - others);
      this.stopCalling();
      this.render();
    },

    // ================================================================ curseur

    bindSlider: function () {
      var range = this.querySelector('[data-linery-range]');
      if (!range) return;
      var tiers = this.pricing.tiers;
      range.max = String(tiers[tiers.length - 1]);
      range.addEventListener('input', function () { this.setTotal(parseInt(range.value, 10)); }.bind(this));
    },

    bindStepper: function () {
      var input = this.querySelector('[data-linery-qty]');
      var minus = this.querySelector('[data-linery-qty-minus]');
      var plus = this.querySelector('[data-linery-qty-plus]');
      if (!input) return;
      var self = this;
      if (minus) minus.addEventListener('click', function () { self.setTotal(self.totalQty() - 1); });
      if (plus) plus.addEventListener('click', function () { self.setTotal(self.totalQty() + 1); });
      input.addEventListener('input', function () { self.setTotal(parseInt(input.value, 10)); });
      input.addEventListener('blur', function () { if (input.value === '') self.render(); });
    },

    placeKnob: function (qty) {
      var range = this.querySelector('[data-linery-range]');
      var knob = this.querySelector('.linery-pc__knob');
      if (!range || !knob) return;
      var max = parseInt(range.max, 10) || 1000;
      var shown = Math.min(qty, max);
      range.value = String(shown);
      var ratio = max ? shown / max : 0;
      knob.style.left = 'calc(' + (ratio * 100).toFixed(3) + '% + ' + ((0.5 - ratio) * 46).toFixed(2) + 'px)';
    },

    // ================================================================ étiquette

    labelValues: function (which) {
      if (!this.labelProduct) return [];
      var i = which === 'color' ? this.labelIdx.color : this.labelIdx.size;
      if (i < 0) i = which === 'color' ? 0 : 1;
      var out = [], seen = {};
      this.labelProduct.variants.forEach(function (v) {
        var val = v.o[i];
        if (val !== undefined && !seen[val]) { seen[val] = true; out.push(val); }
      });
      return out;
    },

    buildLabelOptions: function () {
      if (!this.labelProduct) return;
      var self = this;

      [['color', '[data-linery-label-color]'], ['size', '[data-linery-label-size]']].forEach(function (pair) {
        var which = pair[0];
        var host = self.querySelector(pair[1]);
        if (!host) return;
        host.textContent = '';
        self.labelValues(which).forEach(function (val) {
          var b = el('button', 'linery-pc__option', val);
          b.type = 'button';
          b.setAttribute('role', 'radio');
          b.dataset.value = val;
          b.addEventListener('click', function () {
            self.state.label[which] = val;
            self.refreshLabel();
          });
          host.appendChild(b);
        });
      });

      this.buildModalSizes();
      this.buildTariff();
    },

    /**
     * Le barème des étiquettes, consultable sans ouvrir le tiroir.
     * L'argument commercial n'a de valeur que s'il se voit avant le choix.
     */
    buildTariff: function () {
      var host = this.querySelector('[data-linery-tariff-rows]');
      if (!host) return;
      host.textContent = '';
      this.pricing.tiers.forEach(function (t) {
        var cents = this.pricing.labelPrices[String(t)];
        if (cents === undefined) return;
        var row = el('span', 'linery-pc__tariff-row');
        row.dataset.tier = String(t);
        row.appendChild(el('span', null, this.tr('tier_row', { n: t })));
        row.appendChild(el('b', null, this.prixGrille(cents)));
        host.appendChild(row);
      }, this);
    },

    bindTariff: function () {
      var self = this;
      var btn = this.querySelector('[data-linery-tariff-toggle]');
      var pop = this.querySelector('[data-linery-tariff]');
      if (!btn || !pop) return;
      // Le survol est géré en CSS ; le clic sert au tactile et au clavier.
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        pop.classList.toggle('is-open', !open);
      });
      document.addEventListener('click', function (e) {
        // Meme regle pour la bulle du bareme : un clic ailleurs la ferme,
        // sans avoir a viser la bulle elle-meme.
        if (e.target && e.target.closest && e.target.closest('.linery-pc__tariff')) return;
        btn.setAttribute('aria-expanded', 'false');
        pop.classList.remove('is-open');
      });
    },

    /**
     * Le nuancier du badge « ? ». Il ne liste que les teintes disponibles pour
     * le produit et le grammage courants : afficher une reference que le client
     * ne peut pas commander serait pire que ne rien afficher.
     */
    /**
     * Le nuancier complet, la « Big Pop up » du Figma : les trente-quatre
     * teintes en grand, avec leur reference Pantone. On montre la palette
     * entiere et non les seules teintes commandables — c'est le nuancier de
     * la maison, il ne change pas selon le vetement regarde. Celles qui ne
     * sont pas disponibles sur le modele et le grammage courants sont
     * grisees et ne se cliquent pas : le visiteur voit la gamme sans se
     * tromper de commande.
     */
    buildPantone: function () {
      var host = this.querySelector('[data-linery-pantone-grid]');
      if (!host) return;

      var palette = (this.pricing.raw && this.pricing.raw.colors) || {};
      var dispo = {};
      this.colorValuesFor(this.state.gsm).forEach(function (val) {
        var e = this.paletteEntry(val);
        if (e && /^\d+$/.test(e.code)) dispo[String(parseInt(e.code, 10))] = val;
      }, this);

      // Object.keys ne rend PAS l'ordre d'ecriture : « 10 » a « 34 » sont des
      // index entiers pour JavaScript et passent devant « 01 » a « 09 », qui
      // ne le sont pas a cause du zero de tete. Sans ce tri, le nuancier
      // s'ouvrait sur le brun fonce. C'est le meme piege qui avait fait
      // retomber le calculateur sur le plus petit grammage.
      var numeros = Object.keys(palette).sort(function (a, b) {
        return parseInt(a, 10) - parseInt(b, 10);
      });

      host.textContent = '';
      numeros.forEach(function (num) {
        var c = palette[num];
        var n = String(parseInt(num, 10));

        var carte = el('button', 'linery-pc__pantone-card');
        carte.type = 'button';
        carte.dataset.num = num;
        if (dispo[n]) {
          carte.dataset.value = dispo[n];
          carte.setAttribute('data-linery-pantone-pick', '');
        } else {
          carte.classList.add('is-unavailable');
          carte.disabled = true;
        }

        var pastille = el('span', 'linery-pc__pantone-swatch');
        pastille.style.setProperty('--dot', c.hex);
        carte.appendChild(pastille);

        var meta = el('span', 'linery-pc__pantone-meta');
        var titre = el('span', 'linery-pc__pantone-num');
        titre.appendChild(el('b', null, 'PANTONE'));
        titre.appendChild(el('sup', null, num));
        meta.appendChild(titre);
        meta.appendChild(el('span', 'linery-pc__pantone-ref', c.pantone || '—'));
        meta.appendChild(el('span', 'linery-pc__pantone-label', c.name || ''));
        carte.appendChild(meta);

        host.appendChild(carte);
      }, this);

      this.markPantone();
    },

    markPantone: function () {
      var current = String(this.state.color);
      this.querySelectorAll('[data-linery-pantone-grid] .linery-pc__pantone-card')
        .forEach(function (c) { c.classList.toggle('is-active', c.dataset.value === current); });
    },

    /**
     * Les deux bulles d'aide s'ouvrent au survol avec la meme legerete que la
     * bulle « € » du bareme. Trois choses les separaient d'elle :
     *
     * 1. Un delai de 200 ms avant l'ouverture. La bulle « € » n'attend rien —
     *    c'est du survol CSS pur. On descend a 90 ms : sous le seuil ou l'oeil
     *    percoit une attente, mais assez pour ne pas ouvrir la fenetre au
     *    passage d'une souris qui ne fait que traverser.
     * 2. Aucune transition. « hidden » bascule d'un coup et une fenetre pleine
     *    page apparait sans prevenir. Elle arrive maintenant en fondu, la
     *    boite montant de huit pixels — l'essentiel du gain de fluidite.
     * 3. La fermeture restait a la charge du visiteur. La bulle « € »
     *    disparait des qu'on s'en eloigne ; celle-ci restait plantee la
     *    jusqu'a un clic sur la croix.
     *
     * L'objection notee ici auparavant — « une fenetre qui occupe la moitie de
     * l'ecran ne peut pas se fermer quand la souris la quitte, sinon on ne
     * peut pas y entrer » — tenait tant que la fermeture etait immediate. Elle
     * tombe avec un delai de grace : quitter la bulle arme une fermeture a
     * 420 ms, largement le temps d'atteindre la boite, et y entrer la desarme.
     *
     * Deux gardes-fous. Une ouverture au CLIC ne se referme jamais toute
     * seule : le tactile n'a pas de survol, et un doigt qui ouvre une fenetre
     * qui s'evapore serait incomprehensible. Et un clic DANS la fenetre — une
     * teinte qu'on examine, un texte qu'on selectionne — la fait basculer dans
     * le meme regime : survoler, c'est jeter un oeil ; cliquer, c'est entrer.
     *
     * Un piege trouve au banc d'essai, et qui rendait le survol inutilisable :
     * le fond de la fenetre est fixe et couvre tout l'ecran, BULLE COMPRISE.
     * A la seconde ou elle s'ouvrait, la bulle recevait donc un « mouseleave »
     * qu'aucun geste n'avait provoque, et la fermeture s'armait aussitot — la
     * fenetre se refermait seule au bout de 420 ms, meme souris immobile.
     * Tant qu'on ne fait que jeter un oeil, le fond laisse donc passer les
     * evenements de souris (classe « is-peeking ») : la bulle garde son survol
     * et seule la boite reste sensible. Des qu'on entre pour de bon, le fond
     * redevient normal et la fenetre se comporte comme n'importe quelle autre.
     */
    survolOuvrant: function (bulle, modal) {
      if (!bulle || !modal) return;
      var boite = modal.querySelector('.linery-pc__modal-box');
      var ouverture = null, fermeture = null, parSurvol = false;

      function annuler() { clearTimeout(ouverture); clearTimeout(fermeture); }

      function engager() {
        parSurvol = false;
        modal.classList.remove('is-peeking');
        annuler();
      }

      function ouvrir(survol) {
        annuler();
        parSurvol = !!survol;
        modal.classList.toggle('is-peeking', parSurvol);
        if (!modal.hidden) return;
        modal.hidden = false;
        // Une transition ne part que d'un etat deja calcule : sans cette
        // lecture forcee, le navigateur regroupe les deux changements et la
        // fenetre apparait d'un bloc, sans fondu.
        void modal.offsetWidth;
        modal.classList.add('is-open');
      }

      function fermer() {
        annuler();
        if (modal.hidden) return;
        modal.classList.remove('is-open');
        // On attend la fin du fondu avant de retirer la fenetre du document.
        // Duree calee sur la transition de la feuille de style.
        fermeture = setTimeout(function () {
          modal.hidden = true;
          modal.classList.remove('is-peeking');
        }, 170);
      }

      modal.lmFermer = fermer;

      bulle.addEventListener('mouseenter', function () {
        clearTimeout(fermeture);
        ouverture = setTimeout(function () { ouvrir(true); }, 90);
      });
      bulle.addEventListener('mouseleave', function () {
        clearTimeout(ouverture);
        if (parSurvol && !modal.hidden) fermeture = setTimeout(fermer, 420);
      });
      bulle.addEventListener('click', function () { ouvrir(false); });
      bulle.addEventListener('focus', function () { ouvrir(false); });
      // Un defilement pendant le coup d'oeil veut dire qu'on regarde autre
      // chose : la fenetre s'efface plutot que de suivre la page.
      window.addEventListener('scroll', function () {
        if (parSurvol && !modal.hidden) fermer();
      }, { passive: true });

      if (boite) {
        boite.addEventListener('mouseenter', function () { clearTimeout(fermeture); });
        boite.addEventListener('mouseleave', function () {
          if (parSurvol) fermeture = setTimeout(fermer, 220);
        });
        // Cliquer, c'est entrer : la fenetre cesse de suivre la souris.
        boite.addEventListener('mousedown', engager);
      }
    },

    bindPantone: function () {
      var self = this;
      var modal = this.querySelector('[data-linery-pantone-modal]');
      var ouvrir = this.querySelector('[data-linery-pantone-open]');
      if (!modal || !ouvrir) return;
      var fermer = this.querySelector('[data-linery-pantone-close]');

      this.survolOuvrant(ouvrir, modal);
      var clore = function () { if (modal.lmFermer) modal.lmFermer(); else modal.hidden = true; };

      if (fermer) fermer.addEventListener('click', clore);
      modal.addEventListener('click', function (e) { if (e.target === modal) clore(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) clore();
      });

      // Une teinte cliquee dans le nuancier devient la teinte choisie : le
      // visiteur vient d'y passer du temps, lui demander de la retrouver
      // ensuite dans la liste deroulante serait absurde.
      modal.addEventListener('click', function (e) {
        var carte = e.target && e.target.closest && e.target.closest('[data-linery-pantone-pick]');
        if (!carte) return;
        self.selectColor(carte.dataset.value);
        clore();
      });
    },

    buildModalSizes: function () {
      var host = this.querySelector('[data-linery-modal-sizes]');
      if (!host) return;
      host.textContent = '';
      this.labelValues('size').forEach(function (val) {
        var m = String(val).match(/([\d.,]+)\s*[x×]\s*([\d.,]+)/);
        var row = el('div', 'linery-pc__label-size');
        row.appendChild(el('span', null, val));
        if (m) {
          var w = parseFloat(m[1].replace(',', '.')), h = parseFloat(m[2].replace(',', '.'));
          var box = el('i');
          box.style.width = (w * 11).toFixed(0) + 'px';
          box.style.height = (h * 11).toFixed(0) + 'px';
          row.appendChild(box);
        }
        host.appendChild(row);
      });
    },

    bindLabel: function () {
      var self = this;
      var toggle = this.querySelector('[data-linery-label-toggle]');
      var menu = this.querySelector('[data-linery-label-menu]');
      if (toggle && menu) {
        toggle.addEventListener('click', function () {
          var open = menu.hidden;
          self.closeAllMenus();
          menu.hidden = !open;
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
      }

      var file = this.querySelector('[data-linery-file]');
      var drop = this.querySelector('[data-linery-drop]');
      if (file) {
        file.addEventListener('change', function () { self.acceptFile(file.files[0]); });
      }
      if (drop && this.uploadEnabled) {
        ['dragenter', 'dragover'].forEach(function (ev) {
          drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.add('is-over'); });
        });
        ['dragleave', 'drop'].forEach(function (ev) {
          drop.addEventListener(ev, function (e) { e.preventDefault(); drop.classList.remove('is-over'); });
        });
        drop.addEventListener('drop', function (e) {
          if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) {
            self.acceptFile(e.dataTransfer.files[0]);
          }
        });
      }

      var clearFile = this.querySelector('[data-linery-file-clear]');
      if (clearFile) {
        clearFile.addEventListener('click', function () {
          self.state.label.file = null;
          if (file) file.value = '';
          self.refreshLabel();
        });
      }

      var clearLabel = this.querySelector('[data-linery-label-clear]');
      if (clearLabel) {
        clearLabel.addEventListener('click', function () {
          self.state.label = { color: null, size: null, file: null };
          if (file) file.value = '';
          if (menu) menu.hidden = true;
          self.refreshLabel();
        });
      }
    },

    acceptFile: function (f) {
      var row = this.querySelector('[data-linery-file-row]');
      var name = this.querySelector('[data-linery-file-name]');
      if (!f) return;
      if (f.size > this.uploadMax) {
        this.state.label.file = null;
        if (row) { row.hidden = false; row.classList.add('is-error'); }
        if (name) name.textContent = this.tr('file_too_big', {
          size: (f.size / 1048576).toFixed(1), max: (this.uploadMax / 1048576).toFixed(1),
        });
        return;
      }
      this.state.label.file = f;
      if (row) row.classList.remove('is-error');
      this.refreshLabel();
    },

    labelReady: function () {
      return !!(this.labelProduct && this.state.label.color && this.state.label.size);
    },

    labelVariant: function () {
      if (!this.labelReady()) return null;
      var ci = this.labelIdx.color >= 0 ? this.labelIdx.color : 0;
      var si = this.labelIdx.size >= 0 ? this.labelIdx.size : 1;
      var found = null;
      this.labelProduct.variants.forEach(function (v) {
        if (v.o[ci] === this.state.label.color && v.o[si] === this.state.label.size) found = v;
      }, this);
      return found;
    },

    refreshLabel: function () {
      var self = this;
      ['color', 'size'].forEach(function (which) {
        var sel = which === 'color' ? '[data-linery-label-color]' : '[data-linery-label-size]';
        self.querySelectorAll(sel + ' .linery-pc__option').forEach(function (b) {
          var on = b.dataset.value === String(self.state.label[which]);
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-checked', on ? 'true' : 'false');
        });
      });

      var row = this.querySelector('[data-linery-file-row]');
      var name = this.querySelector('[data-linery-file-name]');
      if (row && !row.classList.contains('is-error')) {
        row.hidden = !this.state.label.file;
        if (name && this.state.label.file) name.textContent = this.state.label.file.name;
      }

      var crumb = this.querySelector('[data-linery-label-crumb]');
      if (crumb) {
        crumb.textContent = '';
        var parts = [];
        if (this.state.label.color) parts.push(this.state.label.color);
        if (this.state.label.size) parts.push(this.state.label.size);
        if (this.state.label.file) parts.push(this.tr('file_uploaded'));
        if (!parts.length) {
          crumb.textContent = crumb.dataset.empty || this.tr('label_none');
        } else {
          parts.forEach(function (p, i) {
            if (i) crumb.appendChild(document.createTextNode(' | '));
            crumb.appendChild(el('b', null, p));
          });
        }
      }

      this.render();
    },

    bindModal: function () {
      var self = this;
      var modal = this.querySelector('[data-linery-modal]');
      var open = this.querySelector('[data-linery-label-help]');
      var close = this.querySelector('[data-linery-modal-close]');
      if (open && modal) this.survolOuvrant(open, modal);
      var clore = function () {
        if (!modal) return;
        if (modal.lmFermer) modal.lmFermer(); else modal.hidden = true;
      };
      if (close && modal) close.addEventListener('click', clore);
      if (modal) {
        modal.addEventListener('click', function (e) { if (e.target === modal) clore(); });
      }
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        if (modal && !modal.hidden) clore();
        else self.closeAllMenus();
      });
      document.addEventListener('click', function (e) {
        // Un clic n'importe ou hors d'un menu deroulant les ferme tous, y
        // compris ailleurs dans la carte. Se limiter a l'exterieur du
        // composant laissait le menu ouvert quand on cliquait sur le fond de
        // la carte ou sur une etiquette : le visiteur croit qu'il est bloque.
        var dansUnSelect = e.target && e.target.closest && e.target.closest('.linery-pc__select');
        if (!dansUnSelect) self.closeAllMenus();
      });
    },

    // ================================================================ affichage

    setText: function (sel, text) { var n = this.querySelector(sel); if (n) n.textContent = text; },
    // Deux selecteurs de produit coexistent en disposition horizontale, un
    // par onglet : ecrire dans le premier trouve n'en mettait qu'un a jour.
    setTextAll: function (sel, text) {
      this.querySelectorAll(sel).forEach(function (n) { n.textContent = text; });
    },
    show: function (sel, on) { var n = this.querySelector(sel); if (n) n.hidden = !on; },

    /** Centimes d'euro de la grille -> centimes de la devise du visiteur. */
    enDevise: function (centimesEuro) {
      return Math.round(centimesEuro * (this.taux || 1));
    },

    /** Un montant de la grille, converti puis mis en forme. */
    prixGrille: function (centimesEuro) {
      return this.money.format(this.enDevise(centimesEuro) / 100);
    },

    render: function () {
      var qty = this.state.pendingQty !== null ? this.state.pendingQty : this.totalQty();
      var q = this.pricing.quote({
        product: this.key,
        gsm: gsmNumber(this.state.gsm),
        sizes: this.state.sizes,
        label: this.labelReady(),
        inCart: this.inCartQty(),
      });
      this.quoteState = q;

      this.markTarget();
      this.placeKnob(qty);

      // L'état fait foi : on le réécrit dans les champs, sauf celui que le
      // client est en train de remplir. Sans cela, déplacer le curseur
      // changerait le calcul sans que la ventilation affichée bouge.
      this.querySelectorAll('[data-linery-sizes] input').forEach(function (i) {
        var v = this.state.sizes[i.dataset.size];
        if (v !== undefined && document.activeElement !== i) i.value = String(v);
      }, this);

      var input = this.querySelector('[data-linery-qty]');
      if (input && document.activeElement !== input) input.value = String(this.totalQty());

      var m = this.prixGrille.bind(this);

      if (!this.approved) { this.updateSubmit(q); return; }

      this.setText('[data-linery-unit]', q.ok && q.quantity > 0 ? m(q.unitPrice) : '—');
      // Sous-total = prix unitaire converti x quantite : c'est ainsi que le
      // panier le calcule (la remise par paliers ramene chaque piece au tarif
      // converti, arrondi au centime). En euros, identique a q.subtotal.
      this.setText('[data-linery-subtotal]', q.ok && q.quantity > 0
        ? this.money.format(this.enDevise(q.unitPrice) * q.quantity / 100) : '—');
      // Deux paliers, regle donnee par Lohan le 09/09 : dix jours ouvres
      // jusqu'a 250 pieces, quinze au-dela. Le seuil et les deux libelles sont
      // des reglages : le jour ou la regle bouge, personne ne touche au code.
      var seuil = parseInt(this.dataset.leadThreshold, 10);
      var delai = this.dataset.leadTime || '—';
      if (seuil > 0 && q.quantity > seuil && this.dataset.leadTimeHigh) {
        delai = this.dataset.leadTimeHigh;
      }
      this.setText('[data-linery-lead]', delai);

      // L'étiquette est un produit à part entière, vendu en complément : elle ne
      // figure pas dans le sous-total du vêtement et apparaît comme n'importe
      // quel autre article dans le panier. C'est exactement ce que montre la
      // maquette — 30 × 15,00 € = 450,00 €, étiquette sélectionnée ou non.
      //
      // Son prix reste visible sur la ligne fermée : tant que rien n'est choisi,
      // on annonce le plancher du barème ; une fois l'étiquette retenue, on
      // affiche le tarif réellement applicable au volume en cours.
      var from = this.querySelector('[data-linery-label-from]');
      if (from) {
        var floorCents = this.pricing.labelPrices[String(this.pricing.tiers[this.pricing.tiers.length - 1])];
        from.textContent = this.labelReady()
          ? this.tr('per_piece', { price: m(q.label.unitPrice) })
          : (floorCents === undefined ? '' : this.tr('from_per_piece', { price: m(floorCents) }));
      }
      this.querySelectorAll('[data-linery-tariff-rows] .linery-pc__tariff-row').forEach(function (r) {
        r.classList.toggle('is-active', q.quantity > 0 && r.dataset.tier === String(q.tier));
      });

      var save = this.querySelector('[data-linery-save]');
      if (save) {
        var on = q.ok && q.saveAmount > 0;
        save.hidden = !on;
        if (on) {
          save.textContent = this.tr('saving', { percent: this.pourcent(q.savePercent), amount: m(q.saveAmount) });
        }
      }

      var nudge = this.querySelector('[data-linery-nudge]');
      if (nudge) {
        if (q.ok && q.next && q.meetsMoq) {
          nudge.hidden = false;
          nudge.classList.toggle('is-strong', !!q.next.cheaperOverall);
          nudge.textContent = q.next.cheaperOverall
            ? this.tr('curiosity', { tier: q.next.tier, total: m(q.next.total), qty: q.quantity })
            : this.trn('nudge', q.next.add, {
              n: q.next.add, price: m(q.next.unitPrice), saving: m(q.next.unitSaving),
            });
        } else {
          nudge.hidden = true;
        }
      }

      var inCartNote = this.querySelector('[data-linery-in-cart]');
      if (inCartNote) {
        inCartNote.hidden = !q.inCart;
        if (q.inCart) {
          inCartNote.textContent = this.trn('in_cart', q.inCart, { n: q.inCart, tier: q.tier, total: q.tierQuantity });
        }
      }

      var moq = this.querySelector('[data-linery-moq]');
      if (moq) {
        var below = q.quantity > 0 && !q.meetsMoq;
        moq.hidden = !below;
        if (below) {
          moq.textContent = this.tr('moq_note', { moq: q.moq, missing: q.missingForMoq });
        }
      }

      this.updateSubmit(q);
    },

    updateSubmit: function (q) {
      var btn = this.querySelector('[data-linery-submit]');
      if (!btn) return;
      var ready = q.ok && q.meetsMoq && q.quantity > 0 && !this.state.busy;
      btn.disabled = !ready;
      var t = this.querySelector('[data-linery-submit-text]');
      if (!t) return;
      // Prêt, le bouton porte l'intitulé de la maquette, sans rien y ajouter :
      // le montant est déjà lisible juste au-dessus, dans la ligne de synthèse.
      if (!this.defaultAddLabel) this.defaultAddLabel = t.textContent.trim() || this.tr('add_default');
      if (this.state.busy) t.textContent = this.tr('busy');
      else if (q.quantity === 0) t.textContent = this.tr('select_quantities');
      else if (!q.meetsMoq) t.textContent = this.tr('min_for_color', { moq: q.moq });
      else t.textContent = this.quoteMode ? (this.dataset.quoteLabel || this.tr('quote')) : this.defaultAddLabel;
    },

    /** Le devis courant, exposé pour les tests et pour d'éventuels scripts tiers. */
    quote: function () { return this.quoteState; },

    // ================================================================ échantillon

    /** Meme regle, meme correction, sur l'onglet echantillon. */
    buildSampleGsm: function () {
      var values = this.gsmValues().filter(Boolean);
      var field = this.querySelector('[data-linery-sample-gsm-field]');
      var host = this.querySelector('[data-linery-sample-gsm]');
      if (host) host.textContent = '';
      if (!values.length) {
        if (field) field.hidden = true;
        return;
      }
      if (field) field.hidden = false;
      if (!host) return;
      values.forEach(function (val) {
        var b = this.gsmChip(val);
        if (values.length === 1) b.classList.add('is-only');
        b.addEventListener('click', function () { this.selectSampleGsm(val); }.bind(this));
        host.appendChild(b);
      }, this);
    },

    selectSampleGsm: function (val, silent) {
      this.state.sample.gsm = val;
      this.markActive('[data-linery-sample-gsm] .linery-pc__chip', val);

      this.wireColorSelect({
        toggle: '[data-linery-sample-color-toggle]',
        menu: '[data-linery-sample-colors]',
        values: this.colorValuesFor(val),
        onSelect: this.selectSampleColor.bind(this),
      });

      var colors = this.colorValuesFor(val);
      this.selectSampleColor(colors.indexOf(this.state.sample.color) !== -1 ? this.state.sample.color : colors[0], true);
      if (!silent) this.renderSample();
    },

    selectSampleColor: function (val, silent) {
      this.state.sample.color = val;
      var entry = this.paletteEntry(val);
      this.setText('[data-linery-sample-color-name]', entry && /^\d+$/.test(entry.code) ? entry.code + ' ' + entry.name : (val || '—'));
      var dot = this.querySelector('[data-linery-sample-color-dot]');
      if (dot) dot.style.setProperty('--dot', entry ? entry.hex : 'transparent');
      this.querySelectorAll('[data-linery-sample-colors] .linery-pc__option').forEach(function (b) {
        b.classList.toggle('is-active', b.dataset.value === String(val));
      });

      this.buildSampleSizes();
      if (!silent) this.renderSample();
    },

    buildSampleSizes: function () {
      var host = this.querySelector('[data-linery-sample-sizes]');
      if (!host) return;
      host.textContent = '';
      var available = [];

      this.matrix.variants.forEach(function (v) {
        if (this.idx.gsm >= 0 && this.state.sample.gsm !== null && v.o[this.idx.gsm] !== this.state.sample.gsm) return;
        if (v.o[this.idx.color] !== this.state.sample.color) return;
        available.push(v);
      }, this);

      available.forEach(function (v) {
        var size = v.o[this.idx.size];
        var b = el('button', 'linery-pc__chip', size);
        b.type = 'button';
        b.setAttribute('role', 'radio');
        b.dataset.value = size;
        if (!this.selectable(v)) b.disabled = true;
        b.addEventListener('click', function () {
          this.state.sample.size = size;
          this.markActive('[data-linery-sample-sizes] .linery-pc__chip', size);
          this.renderSample();
        }.bind(this));
        host.appendChild(b);
      }, this);

      if (available.map(function (v) { return v.o[this.idx.size]; }, this).indexOf(this.state.sample.size) === -1) {
        this.state.sample.size = null;
      }
      this.markActive('[data-linery-sample-sizes] .linery-pc__chip', this.state.sample.size);
    },

    renderSample: function () {
      var variant = this.sampleVariant();

      // Le prix affiche doit etre celui qui sera debite : la fiche Shopify
      // fait foi, le catalogue JSON ne sert que de repli tant qu'aucune
      // variante n'est resolue. Sans cette preference, une retouche de prix
      // dans l'admin afficherait un tarif et en encaisserait un autre.
      var repli = this.pricing.sampleCents(this.key, gsmNumber(this.state.sample.gsm));
      var cents = variant ? variant.price : (repli === null ? null : this.enDevise(repli));
      // Sans le tag « approved », aucun montant : ni dans la ligne de prix,
      // que Liquid ne rend meme pas, ni dans le libelle du bouton, qui
      // l'affichait encore et laissait fuir le tarif.
      if (this.approved) {
        this.setText('[data-linery-sample-price]', cents === null ? '—' : this.money.format(cents / 100));
      }

      var btn = this.querySelector('[data-linery-sample-submit]');
      var txt = this.querySelector('[data-linery-sample-submit-text]');
      if (!btn) return;
      // Sans le tag « approved » le bouton reste inerte : l'echantillon se
      // vend au tarif professionnel, il n'a pas a partir au panier d'un
      // visiteur dont le compte n'est pas valide.
      var plafond = this.plafondAtteint();
      btn.disabled = !this.approved || plafond || !variant || this.state.busy;
      if (!txt) return;
      if (!this.defaultSampleLabel) this.defaultSampleLabel = txt.textContent.trim() || this.tr('add_default');
      if (!this.approved) { txt.textContent = this.tr('trade_access'); return; }
      if (plafond) {
        txt.textContent = this.tr('sample_max', { max: this.sampleMax });
        return;
      }
      if (this.state.busy) txt.textContent = this.tr('busy');
      else if (!this.sampleProduct) txt.textContent = this.tr('sample_unavailable');
      else if (!this.state.sample.size) txt.textContent = this.tr('choose_size');
      else if (!variant) txt.textContent = this.tr('combination_unavailable');
      else if (this.quoteMode) txt.textContent = this.dataset.quoteLabel || this.tr('quote');
      else txt.textContent = this.tr('sample_add', { price: this.money.format((cents || 0) / 100) });
    },

    /**
     * Le libelle de variante de la fiche echantillon, reconstruit depuis
     * linery-pricing.json — la meme source que le script de creation. Une
     * seule verite : si le catalogue bouge, les deux bougent ensemble.
     */
    sampleLabel: function () {
      var entry = this.pricing.entry(this.key, null);
      var g = gsmNumber(this.state.sample.gsm);
      if (!entry || g === null) return null;
      return entry.product.code + ' ' + g + ' g';
    },

    /**
     * Combien d'echantillons du couple courant — ce modele, ce coloris — le
     * panier porte deja.
     *
     * Le plafond etait global : cinq echantillons par commande, toutes
     * familles confondues. Lohan a tranche le 09/09 : « max 6 per color /
     * per model ». Ce n'est pas la meme regle. Un client peut desormais
     * prendre six EDGE noirs ET six EDGE blancs ; ce qu'il ne peut pas, c'est
     * en prendre sept du meme coloris.
     *
     * On compte donc sur la variante — qui EST le modele, axe unique de la
     * fiche echantillon — croisee avec la propriete de coloris. La taille
     * n'entre pas dans le compte : trois tailles du meme coloris sont trois
     * echantillons de ce coloris.
     */
    echantillonsDuCouple: function () {
      var variante = this.varianteDuModele();
      if (!variante || !this.cart) return 0;
      var couleur = this.libelleCouleurEchantillon();
      var total = 0;
      (this.cart.items || []).forEach(function (i) {
        var idVariante = i.variant_id !== undefined ? i.variant_id : i.id;
        if (idVariante !== variante.id) return;
        var p = i.properties || {};
        if (p.Couleur !== couleur) return;
        total += i.quantity;
      });
      return total;
    },

    plafondAtteint: function () {
      return this.sampleMax > 0 && this.echantillonsDuCouple() >= this.sampleMax;
    },

    /**
     * La variante d'echantillon du modele courant, SANS exiger de taille.
     *
     * La fiche echantillon n'a qu'un axe, « Modele » — famille plus grammage.
     * La taille et le coloris voyagent en proprietes de ligne. Une variante
     * est donc entierement determinee par le modele, et le plafond de Lohan
     * — six par coloris et par modele — se compte a ce niveau, sans que la
     * taille entre en jeu. Quelqu'un qui prend un EDGE noir en S, M et L a
     * pris trois echantillons du meme couple, c'est bien le cas a compter.
     */
    varianteDuModele: function () {
      if (!this.sampleProduct) return null;
      var i = this.sampleIdx || {};
      var libelle = this.sampleLabel();
      if (!libelle) return null;
      var found = null;
      var ok = this.selectable.bind(this);
      this.sampleProduct.variants.forEach(function (v) {
        if (found || !ok(v)) return;
        // Axe reconnu : on lit sa valeur. Axe inconnu : on cherche le libelle
        // parmi toutes les valeurs de la variante. Dans les deux cas, une
        // variante n'est retenue que si elle porte EXACTEMENT le modele voulu.
        var valeurs = i.model >= 0 ? [v.o[i.model]] : (v.o || []);
        if (valeurs.indexOf(libelle) !== -1) found = v;
      });
      return found;
    },

    /**
     * Le coloris tel qu'il partira en propriete de ligne. Extrait ici parce
     * que le comptage du plafond doit comparer EXACTEMENT la meme chaine que
     * celle ecrite a l'ajout : deux formulations differentes et le plafond ne
     * reconnaitrait jamais les lignes deja au panier.
     */
    libelleCouleurEchantillon: function () {
      var c = this.paletteEntry(this.state.sample.color);
      return c && /^\d+$/.test(c.code)
        ? c.code + ' ' + c.name
        : String(this.state.sample.color || '—');
    },

    sampleVariant: function () {
      if (!this.sampleProduct || !this.state.sample.size) return null;
      var i = this.sampleIdx, s = this.state.sample, found = null;
      var ok = this.selectable.bind(this);

      // Fiche a axe unique : le prix ne depend que de la famille et du
      // grammage, couleur et taille voyagent en proprietes de ligne. Sans
      // cet appariement, les trois gardes ci-dessous sont toutes inactives
      // et c'est la premiere variante venue qui part au panier.
      if (i.model >= 0) return this.varianteDuModele();

      // L'axe a pu echapper au dictionnaire alors que sa valeur est bien la :
      // on cherche le modele par sa valeur avant toute autre lecture.
      var parValeur = this.varianteDuModele();
      if (parValeur) return parValeur;

      // Fiche a plusieurs axes (grammage, couleur, taille). Chacun doit etre
      // reconnu : un axe inconnu ne filtre rien, et c'est exactement ainsi que
      // la premiere variante venue partait au panier. Mieux vaut un bouton
      // « Combinaison indisponible » qu'un autre vetement a la caisse.
      var reconnus = [i.gsm, i.color, i.size].filter(function (x) { return x >= 0; }).length;
      if (!reconnus || reconnus < (this.sampleProduct.options || []).length) return null;

      this.sampleProduct.variants.forEach(function (v) {
        if (i.gsm >= 0 && s.gsm !== null && v.o[i.gsm] !== s.gsm) return;
        if (i.color >= 0 && v.o[i.color] !== s.color) return;
        if (i.size >= 0 && v.o[i.size] !== s.size) return;
        if (!found && ok(v)) found = v;
      });
      return found;
    },

    // ================================================================ panier

    bindSubmit: function () {
      var btn = this.querySelector('[data-linery-submit]');
      if (btn) btn.addEventListener('click', this.addToCart.bind(this));
    },

    bindSampleSubmit: function () {
      var btn = this.querySelector('[data-linery-sample-submit]');
      if (btn) btn.addEventListener('click', this.addSampleToCart.bind(this));
    },

    /** Identifie la configuration au sens de Lohan : produit, grammage, couleur. */
    configKey: function () {
      return [this.key, this.state.gsm || '-', this.state.color || '-'].join(' | ');
    },

    lineProperties: function (q) {
      var p = {
        '_linery_tier': String(q.tier),
        '_linery_unit_price': String(q.unitPrice),
        '_linery_order_qty': String(q.tierQuantity),
        '_linery_pricing_key': this.key,
        // Cle de lecture de la grille tarifaire cote fonction de remise :
        // "core-160", "fold-340"... Le couple produit + grammage suffit,
        // ni la couleur ni la taille ne font varier le prix.
        '_linery_grid_key': this.key + '-' + gsmNumber(this.state.gsm),
        '_linery_config': this.configKey(),
      };
      if (this.mode !== 'discount_function') {
        p[this.tr('applied_price_name')] = this.tr('applied_price_value', { price: this.prixGrille(q.unitPrice), tier: q.tier });
      }
      return p;
    },

    addToCart: function () {
      var q = this.quoteState;
      if (!q || !q.ok || !q.meetsMoq || this.state.busy) return;
      // Shopify refuserait l'ajout (HTTP 422) : on part vers le devis.
      if (this.quoteMode) return this.demanderUnDevis('commande');

      var bySize = {};
      this.currentVariants().forEach(function (v) { bySize[v.o[this.idx.size]] = v; }, this);

      var items = [], missing = [];
      q.lines.forEach(function (line) {
        var v = bySize[line.size];
        if (!v) { missing.push(line.size); return; }
        items.push({ id: v.id, quantity: line.quantity, properties: this.lineProperties(q) });
      }, this);

      if (missing.length) return this.feedback('[data-linery-feedback]', this.tr('variant_missing', { sizes: missing.join(', ') }), true);
      if (!items.length) return this.feedback('[data-linery-feedback]', this.tr('nothing_to_add'), true);

      this.state.busy = true;
      this.updateSubmit(q);
      this.feedback('[data-linery-feedback]', '', false, true);

      var self = this;
      this.postJson({ items: items })
        .then(function () { return self.postLabel(q); })
        .then(function () {
          self.state.busy = false;
          self.feedback('[data-linery-feedback]', self.tr('added', { n: q.quantity }), false);
          document.dispatchEvent(new CustomEvent('linery:cart:added', { detail: { quote: q } }));
          self.afterAdd(q);
        })
        .catch(function (e) {
          self.state.busy = false;
          self.updateSubmit(q);
          self.feedback('[data-linery-feedback]', e.message || self.tr('add_failed'), true);
        });
    },

    /**
     * L'étiquette part seule, en FormData : c'est la seule forme que /cart/add.js
     * accepte pour un fichier, et elle ne prend qu'un article par requête.
     */
    postLabel: function (q) {
      var variant = this.labelVariant();
      if (!variant) return Promise.resolve();

      var fd = new FormData();
      fd.append('id', String(variant.id));
      fd.append('quantity', String(q.quantity));
      fd.append('properties[_linery_tier]', String(q.tier));
      fd.append('properties[_linery_unit_price]', String(q.label.unitPrice));
      fd.append('properties[_linery_pricing_key]', this.key);
      // "label" : la fonction de remise lit alors le bareme des etiquettes,
      // et exclut cette ligne du volume qui determine le palier — il y a une
      // etiquette par piece, les additionner doublerait la quantite.
      fd.append('properties[_linery_grid_key]', 'label');

      // La configuration d'origine, portée par la ligne d'étiquette. Deux rôles :
      // elle empêche Shopify de fusionner les étiquettes de deux coloris — qui
      // n'ont pas le même palier — et elle dit à la fonction de remise à quelle
      // configuration de vêtement rattacher cette ligne.
      fd.append('properties[_linery_config]', this.configKey());
      fd.append('properties[' + this.tr('prop_for') + ']', [this.state.gsm, this.state.color].filter(Boolean).join(' · '));
      if (this.state.label.file) fd.append('properties[Design]', this.state.label.file);

      // L'etiquette est un second ajout, poste apres le vetement : c'est donc
      // sa reponse qui porte l'etat final du panier. Sans cette demande de
      // section, le tiroir resterait sur l'etat d'avant l'etiquette.
      fd.append('sections', 'cart-drawer');

      var self = this;
      return fetch(routes('cart/add.js'), { method: 'POST', body: fd })
        .then(function (r) {
          return r.json().then(function (j) {
            if (!r.ok) throw new Error((j && j.description) || self.tr('label_failed'));
            var html = j && j.sections && j.sections['cart-drawer'];
            if (html) self.paintDrawer(html); else self.refreshDrawer();
            return j;
          });
        });
    },

    postJson: function (payload) {
      var self = this;

      // On demande le balisage du tiroir dans le meme appel : c'est l'API de
      // rendu de section de Shopify, et le HTML rendu est forcement posterieur
      // a l'ajout. Sans cela, seul le compteur de l'en-tete est mis a jour en
      // JavaScript et le tiroir garde le balisage « panier vide » calcule par
      // Liquid au chargement, jusqu'au prochain changement de page.
      var corps = {};
      for (var k in payload) {
        if (Object.prototype.hasOwnProperty.call(payload, k)) corps[k] = payload[k];
      }
      corps.sections = 'cart-drawer';

      return fetch(routes('cart/add.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(corps),
      }).then(function (r) {
        return r.json().then(function (j) {
          if (!r.ok) throw new Error((j && j.description) || self.tr('add_refused'));
          var html = j && j.sections && j.sections['cart-drawer'];
          // Repli : si la section n'est pas revenue, on va la chercher, plutot
          // que de laisser le tiroir mentir.
          if (html) self.paintDrawer(html); else self.refreshDrawer();
          return j;
        });
      });
    },

    addSampleToCart: function () {
      // Deuxieme verrou, apres le bouton desactive : un clic simule ne doit
      // pas passer non plus. Le vrai verrou reste cote caisse.
      if (!this.approved) return;
      if (this.quoteMode) return this.demanderUnDevis('echantillon');
      if (this.plafondAtteint()) {
        return this.feedback('[data-linery-sample-feedback]',
          this.trn('sample_limit', this.sampleMax, { max: this.sampleMax }), true);
      }
      var variant = this.sampleVariant();
      if (!variant || this.state.busy) return;
      // Dernier garde-fou : sur la fiche a axe unique, la variante envoyee doit
      // porter le libelle du modele affiche au client. Sinon rien ne part.
      var attendu = this.sampleLabel();
      if ((this.sampleProduct.options || []).length === 1 && attendu && (variant.o || [])[0] !== attendu) {
        return this.feedback('[data-linery-sample-feedback]',
          this.tr('sample_not_found', { model: attendu }), true);
      }
      this.state.busy = true;
      this.renderSample();

      var self = this;
      // Un échantillon par combinaison, jamais deux : la quantité est verrouillée à 1.
      // La fiche echantillon ne porte ni couleur ni taille en variantes :
      // sans ces deux proprietes, la commande ne dit pas quoi mettre dans
      // le colis.
      var proprietes = { '_linery_sample': 'true' };
      proprietes[this.tr('prop_color')] = this.libelleCouleurEchantillon();
      proprietes[this.tr('prop_size')] = String(this.state.sample.size || '—');

      this.postJson({ items: [{ id: variant.id, quantity: 1, properties: proprietes }] })
        .then(function () {
          self.state.busy = false;
          self.feedback('[data-linery-sample-feedback]', self.tr('sample_added'), false);
          self.afterAdd(null);
        })
        .catch(function (e) {
          self.state.busy = false;
          self.renderSample();
          self.feedback('[data-linery-sample-feedback]', e.message || self.tr('sample_failed'), true);
        });
    },

    /**
     * Après l'ajout, le client reste sur la page et enchaîne les coloris.
     * Une commande de merch est multi-coloris par nature : renvoyer au panier
     * à chaque ajout casserait exactement le geste qu'on veut encourager.
     */
    afterAdd: function (q) {
      var mode = this.dataset.afterAdd || 'recap';
      if (mode === 'cart') { window.location.href = routes('cart'); return; }
      if (mode === 'checkout') { window.location.href = '/checkout'; return; }

      if (q) this.resetQuantities();
      this.refreshCart(mode === 'drawer');
      if (q) this.render(); else this.renderSample();
    },

    /**
     * On vide les quantités mais on garde grammage, couleur, étiquette et
     * fichier : le client qui ajoute une deuxième teinte veut la même
     * configuration, pas tout ressaisir. Sans cette remise à zéro, il
     * commanderait deux fois la même ventilation sans s'en apercevoir.
     */
    resetQuantities: function () {
      for (var s in this.state.sizes) {
        if (Object.prototype.hasOwnProperty.call(this.state.sizes, s)) this.state.sizes[s] = 0;
      }
      this.state.targetSize = null;
      this.state.pendingQty = null;
      this.stopCalling();
    },

    refreshCart: function (openDrawer) {
      var self = this;
      return fetch(routes('cart.js'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          self.cart = cart;
          self.paintCart(cart);
          // Le palier dépend de ce que la configuration pèse déjà au panier.
          if (self.pricing) { self.render(); self.renderSample(); }
          document.dispatchEvent(new CustomEvent('linery:cart:refreshed', { detail: { cart: cart } }));
          if (openDrawer) self.openDrawer();
        })
        .catch(function () { /* le panier n'est pas critique pour la suite */ });
    },

    /** Le fil de la commande, sous les yeux du client, plus les crochets du thème. */
    paintCart: function (cart) {
      var skip = [];
      if (this.labelProduct) skip.push(this.labelProduct.id);
      if (this.sampleProduct) skip.push(this.sampleProduct.id);

      // On ne compte que les vêtements : additionner les étiquettes
      // afficherait 60 pour une commande de 30 pièces.
      var pieces = 0;
      (cart.items || []).forEach(function (i) {
        if (skip.indexOf(i.product_id) === -1) pieces += i.quantity;
      });

      var recap = this.querySelector('[data-linery-cart-recap]');
      if (recap) {
        recap.hidden = !cart.item_count;
        this.setText('[data-linery-cart-recap-text]',
          this.trn('cart_recap', pieces, { n: pieces, total: this.money.format((cart.total_price || 0) / 100) }));
      }

      var self = this;
      document.querySelectorAll('.cart-count').forEach(function (n) {
        n.textContent = String(cart.item_count);
      });
      document.querySelectorAll('.cart-original-total').forEach(function (n) {
        n.textContent = self.money.format((cart.original_total_price || 0) / 100);
      });
    },

    /**
     * Rafraichit le contenu du tiroir du theme.
     *
     * On remplace « .cart-drawer-inner », et rien d'autre : c'est la
     * convention du theme, cf. AjaxProductUpdate dans base.js. Remplacer la
     * section entiere detruirait l'element <cart-drawer> ainsi que les
     * liaisons posees dessus au chargement.
     */
    paintDrawer: function (html) {
      if (!html) return false;
      var frais = new DOMParser().parseFromString(html, 'text/html')
        .querySelector('.cart-drawer-inner');
      var actuel = document.querySelector('.cart-drawer-inner');
      if (!frais || !actuel) return false;
      actuel.replaceWith(frais);

      // Les images injectees n'ont jamais ete vues par le chargeur differe du
      // theme : sans ce rappel, la vignette reste vide dans le tiroir.
      if (typeof window.lazyloading === 'function') {
        frais.querySelectorAll('.lazy-loading-image').forEach(function (n) {
          window.lazyloading(n);
        });
      }
      return true;
    },

    /** Le meme rafraichissement, quand on n'a pas le HTML sous la main. */
    refreshDrawer: function () {
      var self = this;
      return fetch(routes('') + '?sections=cart-drawer', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (sections) { if (sections) self.paintDrawer(sections['cart-drawer']); })
        .catch(function () { /* le tiroir n'est pas critique pour la suite */ });
    },

    /** Le tiroir du thème, rafraîchi puis ouvert. */
    openDrawer: function () {
      return this.refreshDrawer().then(function () {
        // Le theme n'expose pas de methode open() : il ouvre son tiroir en
        // cliquant le declencheur de l'en-tete. L'ancien appel a d.open()
        // sortait toujours par le garde-fou, sans rien ouvrir.
        var toggle = document.querySelector('.section-header-cart-drawer-toggle');
        if (toggle) toggle.click();
      });
    },

    bindSwitch: function () {
      var sel = this.querySelector('[data-linery-switch]');
      if (!sel) return;
      sel.addEventListener('change', function () {
        if (sel.value) window.location.href = sel.value;
      });
    },

    feedback: function (sel, msg, isError, clear) {
      var f = this.querySelector(sel);
      if (!f) return;
      if (clear) { f.hidden = true; f.textContent = ''; return; }
      f.hidden = !msg;
      f.className = 'linery-pc__feedback' + (isError ? ' is-error' : ' is-success');
      f.textContent = msg;
    },
  };

  if (window.customElements && !window.customElements.get('linery-price-calculator')) {
    var El = function () { return Reflect.construct(HTMLElement, [], El); };
    El.prototype = Object.create(HTMLElement.prototype);
    El.prototype.constructor = El;
    Object.assign(El.prototype, Calculator);
    El.prototype.connectedCallback = Calculator.connected;
    Object.setPrototypeOf(El, HTMLElement);
    window.customElements.define('linery-price-calculator', El);
  } else if (!window.customElements) {
    document.addEventListener('DOMContentLoaded', function () {
      document.querySelectorAll('linery-price-calculator').forEach(function (node) {
        Object.assign(node, Calculator);
        Calculator.connected.call(node);
      });
    });
  }
}());
